import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search as SearchIcon, Smartphone, ShieldAlert, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Loader } from '@/components/common/Loader';
import { FilterBar } from '@/components/common/FilterBar';
import { DateRangePicker, presetRange } from '@/components/common/DateRangePicker';
import { JsonToggle } from '@/components/common/JsonToggle';
import { Truncated } from '@/components/common/Truncated';
import { emulatorsApi } from '@/api/endpoints';
import { formatDate } from '@/lib/utils';
import { toast } from '@/store/toast';

const ALL = '__ALL__';

// Mapa reason (sufixo) → rótulo + badge. Alinhado com DetectionClassificationHelper
// no backend. `session` é informativo (presença de emulador NÃO é cheat — doc §9).
const REASONS: Record<string, { label: string; badge: 'destructive' | 'warning' | 'info' | 'secondary' | 'outline'; strong: boolean }> = {
  modified_binary:      { label: 'Binário modificado',          badge: 'destructive', strong: true },
  external_memory_write:{ label: 'Escrita externa de memória',  badge: 'destructive', strong: true },
  injection:            { label: 'Injeção thread/DLL',          badge: 'destructive', strong: true },
  guest_artifact:       { label: 'Artefato in-guest',           badge: 'destructive', strong: true },
  multi_instance:       { label: 'Multi-instância',             badge: 'warning',     strong: false },
  input_automation:     { label: 'Automação de input',          badge: 'warning',     strong: false },
  session:              { label: 'Sessão (informativo)',        badge: 'secondary',   strong: false },
};

function reasonBadge(reason?: string) {
  const r = REASONS[(reason ?? '').toLowerCase()];
  if (!r) return <Badge variant="outline">{reason ?? '—'}</Badge>;
  return <Badge variant={r.badge}>{r.label}</Badge>;
}

export default function EmulatorsPage() {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<any>(null);
  const [topFamilies, setTopFamilies] = useState<any[]>([]);

  const [family, setFamily] = useState(ALL);
  const [reason, setReason] = useState(ALL);
  const [hwid, setHwid] = useState('');
  const [hwidDeb, setHwidDeb] = useState('');
  const [minRisk, setMinRisk] = useState('');
  const [onlyActionable, setOnlyActionable] = useState(false);
  const [historic, setHistoric] = useState(false);
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();
  const [page, setPage] = useState(1);

  useEffect(() => { const t = setTimeout(() => setHwidDeb(hwid.trim()), 400); return () => clearTimeout(t); }, [hwid]);

  // Cabeçalho (summary + top famílias) — carregado uma vez / ao trocar histórico.
  useEffect(() => {
    emulatorsApi.summary().then(setSummary).catch(() => { /* opcional */ });
    emulatorsApi.top(20).then(setTopFamilies).catch(() => { /* opcional */ });
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    emulatorsApi.list({
      family: family === ALL ? undefined : family,
      reason: reason === ALL ? undefined : reason,
      hwid: hwidDeb || undefined,
      minRisk: minRisk ? Number(minRisk) : undefined,
      onlyActionable: onlyActionable || undefined,
      historic,
      from, to,
      page,
      pageSize: 100
    }).then(r => {
      setData(r.items);
      setTotal(r.totalApprox);
    }).catch((e: any) => {
      toast.error('Falha ao carregar emuladores', e?.response?.data?.reason ?? e.message);
    }).finally(() => setLoading(false));
  }, [family, reason, hwidDeb, minRisk, onlyActionable, historic, from, to, page]);

  useEffect(() => { reload(); }, [reload]);

  function clearAll() {
    setFamily(ALL); setReason(ALL); setHwid(''); setMinRisk('');
    setOnlyActionable(false); setHistoric(false); setFrom(undefined); setTo(undefined); setPage(1);
  }

  const active = [
    family !== ALL && { key: 'fam', label: 'Família', value: family, onClear: () => setFamily(ALL) },
    reason !== ALL && { key: 'r', label: 'Motivo', value: REASONS[reason]?.label ?? reason, onClear: () => setReason(ALL) },
    hwidDeb && { key: 'h', label: 'HWID', value: hwidDeb, onClear: () => setHwid('') },
    minRisk && { key: 'risk', label: 'Risco', value: `≥ ${minRisk}`, onClear: () => setMinRisk('') },
    onlyActionable && { key: 'act', label: 'Apenas acionáveis', value: 'sim', onClear: () => setOnlyActionable(false) },
    historic && { key: 'hist', label: 'Histórico', value: '30 dias', onClear: () => setHistoric(false) },
    from && { key: 'f', label: 'De', value: formatDate(from, 'dd/MM HH:mm'), onClear: () => setFrom(undefined) },
    to && { key: 't', label: 'Até', value: formatDate(to, 'dd/MM HH:mm'), onClear: () => setTo(undefined) },
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  return (
    <div>
      <PageHeader
        title="Emuladores"
        subtitle="Proteção host-side de jogos rodando em emuladores Android (CodeShield Emulator Guard). Usar emulador não é cheat — só tampering, binário modificado e artefato in-guest são acionáveis."
        actions={<JsonToggle data={data} label="Ver JSON" />}
      />

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><Smartphone className="h-3 w-3" /> Famílias ativas</CardTitle></CardHeader><CardContent className="text-2xl font-semibold tabular-nums">{summary.families ?? 0}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Detecções (7d)</CardTitle></CardHeader><CardContent className="text-2xl font-semibold tabular-nums">{summary.totalDetections ?? 0}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><ShieldAlert className="h-3 w-3 text-destructive" /> Acionáveis</CardTitle></CardHeader><CardContent className="text-2xl font-semibold tabular-nums text-destructive">{summary.actionableDetections ?? 0}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1"><Info className="h-3 w-3" /> Informativas</CardTitle></CardHeader><CardContent className="text-2xl font-semibold tabular-nums text-muted-foreground">{summary.informationalDetections ?? 0}</CardContent></Card>
        </div>
      )}

      {/* Top famílias (rollup) */}
      {topFamilies.length > 0 && (
        <Card className="mb-4">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Top emuladores (7 dias)</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Família</TableHead>
                  <TableHead className="text-right">Detecções</TableHead>
                  <TableHead className="text-right">Acionáveis</TableHead>
                  <TableHead className="text-right">Máquinas</TableHead>
                  <TableHead className="text-right">Risco máx.</TableHead>
                  <TableHead>Última detecção</TableHead>
                  <TableHead className="sr-only">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topFamilies.map((f, i) => (
                  <TableRow key={f._id || i}>
                    <TableCell className="font-medium">{f.emulatorFamily ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{f.totalDetections ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {(f.actionableDetections ?? 0) > 0
                        ? <Badge variant="destructive">{f.actionableDetections}</Badge>
                        : <span className="text-muted-foreground">0</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{f.uniqueHwidCount ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge variant={(f.riskScoreMax ?? 0) >= 80 ? 'destructive' : (f.riskScoreMax ?? 0) >= 40 ? 'warning' : 'secondary'}>{f.riskScoreMax ?? 0}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(f.lastSeenAtUtc)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" title="Filtrar por esta família" onClick={() => { setFamily(f.emulatorFamily); setPage(1); }}>
                        <SearchIcon className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <FilterBar active={active} onClearAll={clearAll}>
        <Input className="h-8 w-72" placeholder="Buscar por HWID…" value={hwid} onChange={e => { setHwid(e.target.value); setPage(1); }} />
        <Select value={family} onValueChange={v => { setFamily(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Família" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas as famílias</SelectItem>
            <SelectItem value="LDPlayer">LDPlayer</SelectItem>
            <SelectItem value="MEmu">MEmu</SelectItem>
            <SelectItem value="NoxPlayer">NoxPlayer</SelectItem>
            <SelectItem value="MuMu">MuMu</SelectItem>
            <SelectItem value="BlueStacks">BlueStacks</SelectItem>
            <SelectItem value="Gameloop">Gameloop</SelectItem>
          </SelectContent>
        </Select>
        <Select value={reason} onValueChange={v => { setReason(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-52 text-xs"><SelectValue placeholder="Motivo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os motivos</SelectItem>
            {Object.entries(REASONS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input className="h-8 w-28" type="number" min={0} placeholder="Risco ≥" value={minRisk} onChange={e => { setMinRisk(e.target.value); setPage(1); }} />
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">
          <input type="checkbox" checked={onlyActionable} onChange={e => { setOnlyActionable(e.target.checked); setPage(1); }} />
          Apenas acionáveis
        </label>
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">
          <input type="checkbox" checked={historic} onChange={e => { setHistoric(e.target.checked); setPage(1); }} />
          Incluir histórico 30 dias
        </label>
        <DateRangePicker from={from} to={to} onChange={r => { setFrom(r.from); setTo(r.to); setPage(1); }} />
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last24h'); setFrom(r.from); setTo(r.to); }}>24h</Button>
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last7d'); setFrom(r.from); setTo(r.to); }}>7d</Button>
        </div>
      </FilterBar>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Família</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead>Risco</TableHead>
              <TableHead>HWID</TableHead>
              <TableHead>Binário / assinante</TableHead>
              <TableHead>Artefato in-guest</TableHead>
              <TableHead>Quando</TableHead>
              <TableHead className="sr-only">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8}><Loader /></TableCell></TableRow>}
            {!loading && data.length === 0 && <TableRow><TableCell colSpan={8}><EmptyState title="Nenhuma detecção de emulador" description="Ajuste os filtros ou aguarde novos eventos." /></TableCell></TableRow>}
            {data.map((d, i) => (
              <TableRow key={d._id || i}>
                <TableCell className="font-medium">{d.emulatorFamily ?? '—'}{d.emulatorVersion ? <span className="text-[10px] text-muted-foreground ml-1">{d.emulatorVersion}</span> : null}</TableCell>
                <TableCell>{reasonBadge(d.detectionReason)}</TableCell>
                <TableCell><Badge variant={(d.riskScore ?? 0) >= 80 ? 'destructive' : (d.riskScore ?? 0) >= 40 ? 'warning' : 'secondary'}>{d.riskScore ?? 0}</Badge></TableCell>
                <TableCell className="text-xs"><Truncated mono value={d.hardwareId} maxChars={18} /></TableCell>
                <TableCell className="text-xs">
                  {d.binarySigner ? <div><Truncated value={d.binarySigner} maxChars={28} /></div> : <span className="text-muted-foreground">não assinado</span>}
                  {d.binaryHash && <div className="text-[10px] text-muted-foreground">SHA: <Truncated mono value={d.binaryHash} maxChars={16} /></div>}
                </TableCell>
                <TableCell className="text-xs">{d.guestArtifact ? <Badge variant="destructive"><Truncated value={d.guestArtifact} maxChars={20} /></Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(d.ts)}</TableCell>
                <TableCell>
                  {d.hardwareId && (
                    <Link to={`/drill?type=hwid&value=${encodeURIComponent(d.hardwareId)}`}>
                      <Button size="sm" variant="ghost" title="Detalhar máquina"><SearchIcon className="h-3 w-3" /></Button>
                    </Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
        <span>Página {page} · {data.length} de {total} no total</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading}>Anterior</Button>
          <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={data.length < 100 || loading}>Próxima</Button>
        </div>
      </div>
    </div>
  );
}
